import { useState, useEffect } from 'react';
import { useSearchParamsWithApiKey } from '../../tools';

interface useAdaptToProductParams {
    sourceProductName: string;
    sourceDocumentUrl: string;
    targetProductName: string;
    targetSurfaceSpecificationUrl: string;
    sourceSurfaceSpecificationUrl: string;
    targetSubstrateColor: string;
    culture: string;
    setSourceProductName: (sourceProductName: string) => void;
    setSourceDocumentUrl: (sourceDocumentUrl: string) => void;
    setTargetProductName: (targetProductName: string) => void;
    setTargetSurfaceSpecificationUrl: (targetSurfaceSpecificationUrl: string) => void;
    setSourceSurfaceSpecificationUrl: (sourceSurfaceSpecificationUrl: string) => void;
    setTargetSubstrateColor: (targetSubstrateColor: string) => void;
    setCulture: (culture: string) => void;
}

export const useAdaptToProduct = (): useAdaptToProductParams => {
    const [searchParams, setSearchParams] = useSearchParamsWithApiKey();

    const sourceProductNameParam = searchParams.get('sourceProductName');
    const sourceDocumentUrlParam = searchParams.get('sourceDocumentUrl');
    const sourceSurfaceSpecificationUrlParam = searchParams.get('sourceSurfaceSpecificationUrl');
    const targetProductNameParam = searchParams.get('targetProductName');
    const targetSurfaceSpecificationUrlParam = searchParams.get('targetSurfaceSpecificationUrl');
    const targetSubstrateColorParam = searchParams.get('targetSubstrateColor');
    const cultureParam = searchParams.get('culture');

    const [sourceProductName, setSourceProductName] = useState(sourceProductNameParam || '');
    const [sourceDocumentUrl, setSourceDocumentUrl] = useState(sourceDocumentUrlParam || '');
    const [sourceSurfaceSpecificationUrl, setSourceSurfaceSpecificationUrl] = useState(
        sourceSurfaceSpecificationUrlParam || '',
    );
    const [targetProductName, setTargetProductName] = useState(targetProductNameParam || '');
    const [targetSurfaceSpecificationUrl, setTargetSurfaceSpecificationUrl] = useState(
        targetSurfaceSpecificationUrlParam || '',
    );
    const [targetSubstrateColor, setTargetSubstrateColor] = useState(targetSubstrateColorParam || 'rgb(255, 255, 255)');
    const [culture, setCulture] = useState(cultureParam || 'en-us');

    useEffect(() => {
        const searchParamsObj: Record<string, string> = {};

        if (sourceProductName) {
            searchParamsObj['sourceProductName'] = sourceProductName;
        }

        if (sourceDocumentUrl) {
            searchParamsObj['sourceDocumentUrl'] = sourceDocumentUrl;
        }

        if (targetProductName) {
            searchParamsObj['targetProductName'] = targetProductName;
        }

        if (targetSurfaceSpecificationUrl) {
            searchParamsObj['targetSurfaceSpecificationUrl'] = targetSurfaceSpecificationUrl;
        }

        if (sourceSurfaceSpecificationUrl) {
            searchParamsObj['sourceSurfaceSpecificationUrl'] = sourceSurfaceSpecificationUrl;
        }

        if (targetSubstrateColor && targetSubstrateColor !== 'rgb(255, 255, 255)') {
            searchParamsObj['targetSubstrateColor'] = targetSubstrateColor;
        }

        if (culture && culture !== 'en-us') {
            searchParamsObj['culture'] = culture;
        }

        setSearchParams(searchParamsObj);
    }, [
        sourceProductName,
        sourceDocumentUrl,
        sourceSurfaceSpecificationUrl,
        targetProductName,
        targetSurfaceSpecificationUrl,
        targetSubstrateColor,
        culture,
        setSearchParams,
    ]);

    return {
        sourceProductName,
        sourceDocumentUrl,
        sourceSurfaceSpecificationUrl,
        targetProductName,
        targetSurfaceSpecificationUrl,
        targetSubstrateColor,
        culture,
        setSourceProductName,
        setSourceDocumentUrl,
        setSourceSurfaceSpecificationUrl,
        setTargetProductName,
        setTargetSurfaceSpecificationUrl,
        setTargetSubstrateColor,
        setCulture,
    };
};
